package fr.frux.shared.data.local

import fr.frux.shared.domain.model.Site

/**
 * Lot de lieux lu depuis le cache local. [isStale] vaut vrai quand le plus ancien `cached_at`
 * dépasse [TreeCachePolicy.staleAfterMillis], jamais sur un lot vide.
 */
data class CachedSites(
    val sites: List<Site>,
    val isStale: Boolean,
)

/**
 * Source locale du cache des lieux (ADR 0035), lue avant le réseau puis réécrite après chaque
 * succès, sur le patron de [TreeLocalDataSource]. Un lieu relu n'a ni arbres rattachés ni
 * drapeaux du lecteur (favori, propriétaire, suppression, signalement) : ils sont propres à la
 * session et restent réseau.
 */
interface SiteLocalDataSource {
    /** Lieux du cache dans un rayon de [radiusKm] km, du plus proche au plus lointain. */
    suspend fun nearbySites(
        lat: Double,
        lon: Double,
        radiusKm: Double,
    ): CachedSites

    /** Un lieu du cache par id, `null` s'il est absent, expiré ou illisible. */
    suspend fun site(id: Long): Site?

    /** Insère ou remplace [sites] et leurs plantations en une seule transaction. */
    suspend fun upsertAll(sites: List<Site>)

    /** Supprime les lieux expirés puis ramène le cache sous [TreeCachePolicy.maxSiteRows]. */
    suspend fun prune()

    /** Retire un lieu et ses plantations, après sa suppression côté serveur. */
    suspend fun delete(id: Long)
}

/** Sans stockage (web) : lectures vides, écritures ignorées, le repository reste réseau seul. */
object NoOpSiteLocalDataSource : SiteLocalDataSource {
    override suspend fun nearbySites(
        lat: Double,
        lon: Double,
        radiusKm: Double,
    ): CachedSites = CachedSites(sites = emptyList(), isStale = false)

    override suspend fun site(id: Long): Site? = null

    override suspend fun upsertAll(sites: List<Site>) = Unit

    override suspend fun prune() = Unit

    override suspend fun delete(id: Long) = Unit
}
